import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "../../utils/classNames";


interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  className?: string;
}

export default function Tooltip({
  content,
  children,
  position = "top",
  className
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const [arrowPos, setArrowPos] = useState<{ left?: number; top?: number }>({});
  // The side actually used after auto-flip; may differ from the `position` prop.
  const [effectivePosition, setEffectivePosition] = useState<TooltipProps["position"]>(position);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Positioning (which edge the arrow sits on) only — the cross-axis offset is
  // applied inline so the arrow can keep pointing at the trigger after clamping.
  const arrowClasses = {
    top: "top-full border-t-gray-900 border-l-transparent border-r-transparent border-b-transparent",
    bottom: "bottom-full border-b-gray-900 border-l-transparent border-r-transparent border-t-transparent",
    left: "left-full border-l-gray-900 border-t-transparent border-b-transparent border-r-transparent",
    right: "right-full border-r-gray-900 border-t-transparent border-b-transparent border-l-transparent",
  };

  const computePosition = () => {
    const trigger = wrapperRef.current;
    const tip = tooltipRef.current;
    if (!trigger || !tip) return;

    const t = trigger.getBoundingClientRect();
    const w = tip.offsetWidth;
    const h = tip.offsetHeight;
    const gap = 8;
    const margin = 8; // keep this far from the viewport edges
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Auto-flip to the opposite side when the preferred side lacks room but the
    // opposite side has more. Space available between the trigger and each edge:
    const opposite = { top: "bottom", bottom: "top", left: "right", right: "left" } as const;
    const space = {
      top: t.top,
      bottom: vh - t.bottom,
      left: t.left,
      right: vw - t.right,
    };
    const needed = (pos: NonNullable<TooltipProps["position"]>) =>
      pos === "top" || pos === "bottom" ? h + gap : w + gap;

    let effective = position ?? "top";
    if (space[effective] < needed(effective) && space[opposite[effective]] > space[effective]) {
      effective = opposite[effective];
    }

    let top = 0;
    let left = 0;
    switch (effective) {
      case "bottom":
        top = t.bottom + gap;
        left = t.left + t.width / 2 - w / 2;
        break;
      case "left":
        top = t.top + t.height / 2 - h / 2;
        left = t.left - w - gap;
        break;
      case "right":
        top = t.top + t.height / 2 - h / 2;
        left = t.right + gap;
        break;
      case "top":
      default:
        top = t.top - h - gap;
        left = t.left + t.width / 2 - w / 2;
        break;
    }

    // Clamp to the viewport so the tooltip never renders off-screen.
    // (max() guards the case where the tooltip is wider/taller than the viewport.)
    const clampedLeft = Math.min(Math.max(margin, left), Math.max(margin, vw - w - margin));
    const clampedTop = Math.min(Math.max(margin, top), Math.max(margin, vh - h - margin));

    // Re-point the arrow at the trigger centre after any clamp shift.
    const triggerCenterX = t.left + t.width / 2;
    const triggerCenterY = t.top + t.height / 2;
    if (effective === "top" || effective === "bottom") {
      setArrowPos({ left: Math.min(Math.max(8, triggerCenterX - clampedLeft), w - 8) });
    } else {
      setArrowPos({ top: Math.min(Math.max(8, triggerCenterY - clampedTop), h - 8) });
    }

    setEffectivePosition(effective);
    setCoords({ top: clampedTop, left: clampedLeft });
  };

  useLayoutEffect(() => {
    if (!shouldRender) return;
    computePosition();
    // Recompute if layout shifts while shown (scroll/resize)
    const onUpdate = () => computePosition();
    window.addEventListener("scroll", onUpdate, true);
    window.addEventListener("resize", onUpdate);
    return () => {
      window.removeEventListener("scroll", onUpdate, true);
      window.removeEventListener("resize", onUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender, position, content]);

  const handleMouseEnter = () => {
    setShouldRender(true);
    setTimeout(() => setIsVisible(true), 10);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
    setTimeout(() => {
      setShouldRender(false);
      setCoords(null);
    }, 200);
  };

  return (
    <div
      ref={wrapperRef}
      className={classNames("relative inline-block", className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {shouldRender && typeof document !== "undefined" &&
        createPortal(
          <div
            ref={tooltipRef}
            style={{
              position: "fixed",
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
            }}
            className={classNames(
              "z-50 px-3 py-2 text-sm text-white bg-gray-900 rounded-md shadow-lg whitespace-nowrap pointer-events-none transition-[opacity,transform] duration-200",
              isVisible && coords ? "opacity-100 scale-100" : "opacity-0 scale-95"
            )}
          >
            {content}
            <div
              style={
                effectivePosition === "top" || effectivePosition === "bottom"
                  ? { left: arrowPos.left, transform: "translateX(-50%)" }
                  : { top: arrowPos.top, transform: "translateY(-50%)" }
              }
              className={classNames(
                "absolute w-0 h-0 border-4",
                arrowClasses[effectivePosition ?? "top"]
              )}
            />
          </div>,
          document.body
        )}
    </div>
  );
}

